import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { CanonicalService } from './canonocal.service';

@Injectable({
  providedIn: 'root'
})
export class SeoService {

  private readonly defaultImage = 'https://www.albcode.de/logos/AlbCodeLogo_white.png';
  private readonly defaultDescription = $localize`AlbCode entwickelt maßgeschneiderte Software- und Webprojekte für Unternehmen - von der Idee bis zum stabilen Betrieb.`;
  private readonly siteUrl = 'https://www.albcode.de';

  constructor(
    private title: Title,
    private meta: Meta,
    private canonicalService: CanonicalService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(LOCALE_ID) private localeId: string
  ) { }

  setTitle(title: string): this {
    const sanitizedTitle = title.trim();
    const hasBrand = /albcode/i.test(sanitizedTitle);
    const fullTitle = hasBrand ? sanitizedTitle : `${sanitizedTitle} | AlbCode`;
    this.title.setTitle(fullTitle);

    this.updateMetaTag({ name: 'title', content: fullTitle });
    this.updateMetaTag({ property: 'og:title', content: fullTitle });
    this.updateMetaTag({ name: 'twitter:title', content: fullTitle });

    return this;
  }

  setDescription(description?: string): this {
    const safeDescription = description?.trim() ? description : this.defaultDescription;

    this.updateMetaTag({ name: 'description', content: safeDescription });
    this.updateMetaTag({ property: 'og:description', content: safeDescription });
    this.updateMetaTag({ name: 'twitter:description', content: safeDescription });
    this.updateLanguageMetadata(safeDescription);

    return this;
  }

  setKeywords(keywords: string[]): this {
    const seen = new Set<string>();
    const sanitizedKeywords = keywords
      .map(keyword => keyword.trim())
      .filter(keyword => keyword.length > 1)
      .filter(keyword => {
        const key = keyword.toLocaleLowerCase('de-DE');
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });

    this.updateMetaTag({
      name: 'keywords',
      content: sanitizedKeywords.join(', ')
    });
    return this;
  }

  setImage(imageUrl: string = this.defaultImage): this {
    this.updateMetaTag({ property: 'og:image', content: imageUrl });
    this.updateMetaTag({ name: 'twitter:image', content: imageUrl });
    this.updateMetaTag({ name: 'twitter:card', content: 'summary_large_image' });

    return this;
  }

  setCanonicalUrl(url?: string): void {
    const locale = this.localeId.startsWith('en') ? 'en' : 'de';
    const rawPath = (url ?? '/').split(/[?#]/, 1)[0] || '/';
    const path = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
    const canonicalUrl = locale === 'en'
      ? `${this.siteUrl}/en${path === '/' ? '/' : path}`
      : `${this.siteUrl}${path}`;
    this.updateMetaTag({ property: 'og:url', content: canonicalUrl });
    this.canonicalService.setCanonicalURL(url);
  }

  private updateLanguageMetadata(description: string): void {
    const isEnglish = this.localeId.startsWith('en');
    this.document.documentElement.lang = isEnglish ? 'en' : 'de';

    const contentLanguage = this.document.head.querySelector<HTMLMetaElement>('meta[http-equiv="Content-Language"]');
    if (contentLanguage) contentLanguage.content = isEnglish ? 'en' : 'de';

    this.updateMetaTag({ property: 'og:locale', content: isEnglish ? 'en_US' : 'de_DE' });
    this.updateMetaTag({ property: 'og:locale:alternate', content: isEnglish ? 'de_DE' : 'en_US' });

    const structuredData = this.document.head.querySelector<HTMLScriptElement>('script[type="application/ld+json"]');
    if (!structuredData?.textContent) return;
    try {
      const data = JSON.parse(structuredData.textContent);
      const graph = Array.isArray(data['@graph']) ? data['@graph'] : [];
      for (const entry of graph) {
        if (entry['@type'] === 'Organization') entry.description = description;
        if (['Organization', 'WebSite'].includes(entry['@type'])) entry.url = `${this.siteUrl}${isEnglish ? '/en/' : '/'}`;
        if (entry['@type'] === 'WebSite') entry.inLanguage = isEnglish ? 'en' : 'de';
      }
      structuredData.textContent = JSON.stringify(data);
    } catch {
      // Keep the original structured data if custom markup is not valid JSON.
    }
  }

  private updateMetaTag(tag: MetaDefinition): void {
    if (tag.name) {
      this.meta.updateTag(tag, `name='${tag.name}'`);
    } else if ((tag as any).property) {
      this.meta.updateTag(tag, `property='${(tag as any).property}'`);
    }
  }
}
